import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const AprobadaModal = ({
  open,
  onClose,
  onConfirm,
  iniciativa,
  loading = false
}) => {
  const [importe, setImporte] = useState('');
  const [fechaAprobacion, setFechaAprobacion] = useState('');
  const [errors, setErrors] = useState({});

  // Determinar qué campos faltan
  const faltaImporte = !iniciativa?.importe_total_cliente;
  const faltaFecha = !iniciativa?.fecha_de_aprobacion_propuesta_firmada;

  useEffect(() => {
    if (open && iniciativa) {
      setImporte(iniciativa.importe_total_cliente || '');
      setFechaAprobacion(iniciativa.fecha_de_aprobacion_propuesta_firmada || '');
      setErrors({});
    }
  }, [open, iniciativa]);

  const validate = () => {
    const newErrors = {};
    if (faltaImporte && !importe) {
      newErrors.importe = 'El importe de venta es obligatorio para aprobar';
    }
    if (faltaImporte && importe && isNaN(Number(importe))) {
      newErrors.importe = 'El importe debe ser un número válido';
    }
    if (faltaFecha && !fechaAprobacion) {
      newErrors.fechaAprobacion = 'La fecha de aprobación es obligatoria para aprobar';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    const data = {};
    if (faltaImporte) data.importe_total_cliente = Number(importe);
    if (faltaFecha) data.fecha_de_aprobacion_propuesta_firmada = fechaAprobacion;
    onConfirm(data);
  };

  const camposFaltantes = [];
  if (faltaImporte) camposFaltantes.push('Importe de venta');
  if (faltaFecha) camposFaltantes.push('Fecha de aprobación');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">Campos requeridos para Aprobar</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Para mover la iniciativa al estado <strong>Aprobada</strong> es necesario
          completar los siguientes campos:{' '}
          <strong>{camposFaltantes.join(' y ')}</strong>.
        </Alert>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {faltaImporte && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Importe de venta (€)"
                type="number"
                value={importe}
                onChange={(e) => {
                  setImporte(e.target.value);
                  if (errors.importe) setErrors((prev) => ({ ...prev, importe: '' }));
                }}
                error={!!errors.importe}
                helperText={errors.importe || 'Importe total facturado al cliente'}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={loading}
                variant="outlined"
              />
            </Grid>
          )}
          {faltaFecha && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fecha de aprobación"
                type="date"
                value={fechaAprobacion}
                onChange={(e) => {
                  setFechaAprobacion(e.target.value);
                  if (errors.fechaAprobacion)
                    setErrors((prev) => ({ ...prev, fechaAprobacion: '' }));
                }}
                error={!!errors.fechaAprobacion}
                helperText={errors.fechaAprobacion || 'Fecha en que se firmó la propuesta'}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                variant="outlined"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color="success"
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Guardando...' : 'Guardar y Aprobar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AprobadaModal;
